import { usePopup } from '@/features/Popup/usePopup';

import { useCallback } from 'react';
import {
  applySoftReloadPreset,
  type SoftReloadPreset,
} from './softReloadPresets';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

let isReloading = false;

export const isSoftReloadInProgress = (): boolean => isReloading;

export type SoftReloadOptions = {
  preset?: SoftReloadPreset;
  forceIframeReload?: boolean;
  /** Keeps the current launch envelope open across a same-game silent relaunch. */
  preserveLaunchVitals?: boolean;
  /** @default true */
  hidePopup?: boolean;
};

type ReloadArg = boolean | SoftReloadOptions | undefined;

const normalizeReloadArgs = (arg?: ReloadArg): SoftReloadOptions => {
  if (arg === true) {
    return { preset: 'logout', hidePopup: true };
  }
  if (arg === false || arg === undefined) {
    return { preset: 'iframe-only', hidePopup: true };
  }
  return { preset: 'iframe-only', hidePopup: true, ...arg };
};

const useSoftReload = () => {
  const { hidePopup } = usePopup();

  const initReload = useCallback(async (options: SoftReloadOptions) => {
    const {
      preset = 'iframe-only',
      forceIframeReload = false,
      preserveLaunchVitals = false,
    } = options;
    if (isReloading) {
      return;
    }

    isReloading = true;

    try {
      await applySoftReloadPreset(
        preset,
        forceIframeReload,
        preserveLaunchVitals,
      );
    } finally {
      isReloading = false;
    }
  }, []);

  const reload = useCallback(
    (arg?: ReloadArg) => {
      const options = normalizeReloadArgs(arg);

      if (options.hidePopup !== false) {
        hidePopup();
      }
      Logger.logRumAction({
        name: '[GW][RELOAD] Perform soft reload',
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Info,
        payload: {
          options,
        },
      });
      initReload(options);
    },
    [hidePopup, initReload],
  );

  return { reload };
};

export default useSoftReload;
export type { SoftReloadPreset } from './softReloadPresets';
